import type { MenuItemDef } from "ag-grid-community";
import { useGridMenuItem } from "ag-grid-react";

import { IconClose, IconRefresh, IconResizeColumn, IconSettings, IconSortAsc, IconSortDesc } from "@synthesis/shared-icons";

type CustomMenuItemProps = {
  name?: string;
  icon?: React.ReactNode;
  api: {
    resetColumnState: () => void;
    applyColumnState: (state: { state: { colId: string; sort: string }[] }) => void;
    autoSizeAllColumns: () => void;
    showColumnChooser: () => void;
  };
  columnState?: { state: { colId: string; sort: string }[] };
};

export const CustomMenuItem = (props: CustomMenuItemProps) => {
  useGridMenuItem({
    configureDefaults: () => true,
  });

  const sortFn = () => {
    if (props.name === "Сортировать по возрастанию" || props.name === "Сортировать по убыванию" || props.name === "Очистить сортировку") {
      props.api.resetColumnState();
      if (props.columnState) {
        props.api.applyColumnState(props.columnState);
      }
    }
  };

  return (
    <div
      className="hover:bg-grey-300 paragraph-base flex min-w-72 items-center gap-4 bg-white p-3 text-black outline-none hover:border-opacity-50"
      onClick={sortFn}
    >
      <span>{props.icon}</span>
      <span>{props.name}</span>
    </div>
  );
};

const CustomSeparator = () => {
  return <div className="bg-grey-300 h-px" />;
};

const getProjectPageSort = (name: string, sortDirection: "asc" | "desc" | "unsorted") => {
  let columnState: { state: { colId: string; sort: string }[] } = { state: [] };

  switch (sortDirection) {
    case "asc":
      columnState.state = [{ colId: name, sort: "asc" }];
      return {
        name: "Сортировать по возрастанию",
        columnState,
        menuItem: CustomMenuItem,
        icon: <IconSortAsc className="text-neutrals-60 size-6" />,
      };
    case "desc":
      columnState.state = [{ colId: name, sort: "desc" }];
      return {
        name: "Сортировать по убыванию",
        columnState,
        menuItem: CustomMenuItem,
        icon: <IconSortDesc className="text-neutrals-60 size-6" />,
      };
    case "unsorted":
      columnState.state = [{ colId: name, sort: "unsorted" }];
      return {
        name: "Очистить сортировку",
        columnState,
        menuItem: CustomMenuItem,
        icon: <IconClose className="text-neutrals-60 size-6" />,
      };
    default:
      return {
        name: "",
        columnState: { state: [] },
        menuItem: CustomMenuItem,
      };
  }
};

export const getDynamicMainMenuItems = (params: { column: { getSort: () => "asc" | "desc" | null | undefined } }, name: string) => {
  const currentSort = params.column.getSort();

  let menuItems = [...defaultProjectPageMenuItems];

  if (!currentSort) {
    menuItems = [getProjectPageSort(name, "asc"), getProjectPageSort(name, "desc"), ...menuItems] as MenuItemDef[];
  } else {
    menuItems = (
      currentSort === "asc"
        ? [getProjectPageSort(name, "desc"), getProjectPageSort(name, "unsorted"), ...menuItems]
        : [getProjectPageSort(name, "asc"), getProjectPageSort(name, "unsorted"), ...menuItems]
    ) as MenuItemDef[];
  }

  return menuItems as MenuItemDef[];
};

export const defaultProjectPageMenuItems = [
  {
    name: "Сепаратор",
    menuItem: CustomSeparator,
  },
  {
    name: "Автоматически задавать размер всем столбцам",
    action: (props: { api: { autoSizeAllColumns: () => void } }) => props.api.autoSizeAllColumns(),
    menuItem: CustomMenuItem,
    icon: <IconResizeColumn className="text-neutrals-60 size-6" />,
  },
  {
    name: "Сепаратор",
    menuItem: CustomSeparator,
  },
  {
    name: "Выбрать столбцы",
    action: (props: { api: { showColumnChooser: () => void } }) => props.api.showColumnChooser(),
    menuItem: CustomMenuItem,
    icon: <IconSettings className="text-neutrals-60 size-6" />,
  },
  {
    name: "Сбросить столбцы",
    action: (props: { api: { resetColumnState: () => void } }) => props.api.resetColumnState(),
    menuItem: CustomMenuItem,
    icon: <IconRefresh className="text-neutrals-60 size-6" />,
  },
] as MenuItemDef[];
